import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { WorkflowView } from '@/lib/api/generated/model';

import { ANY_STEP } from '../types';
import { allWorkflowSteps } from '../workflowSteps';

const ANY_STEP_LABEL = 'Any step';

/**
 * Pick one step within an already-resolved workflow.
 *
 * Half of the watched-transition/target-step picker (research.md D3) — see
 * {@link WorkflowSelect} for the workflow half. Composed four times per
 * `TriggerProfileFormSheet`/`TriggerActionFields`: `workspace_task_change`'s
 * from-step and to-step (`allowAnyStep`, spec 074 US5), and the create-task/
 * continue-task actions' own target step (never `allowAnyStep` — a target
 * step is where a task actually gets created/moved to, so it always has to
 * name a real one). Disabled/empty until `workflow` is provided.
 */
export function WorkflowStepSelect({
  workflow,
  value,
  onChange,
  disabled,
  allowAnyStep,
  humanGateOnly,
  'aria-label': ariaLabel,
}: {
  workflow: WorkflowView | undefined;
  value: string | undefined;
  onChange: (stepId: string) => void;
  disabled?: boolean;
  /** Adds the reserved "Any step" entry (spec 074, US5, FR-018/FR-020) —
   *  only ever set for `workspace_task_change`'s `from_step_id`/
   *  `to_step_id` selectors, never for an action's own target step. */
  allowAnyStep?: boolean;
  /** Lists only `human_gate` steps (spec 076 US4/FR-016) — a `continue_task`
   *  action's own target step, and only that one: the backend rejects any
   *  other step kind there, so offering it here would just be a picker the
   *  server always refuses. */
  humanGateOnly?: boolean;
  'aria-label'?: string;
}) {
  const steps = (workflow ? allWorkflowSteps(workflow) : []).filter(
    (step) => !humanGateOnly || step.type === 'human_gate',
  );

  const placeholder = !workflow ? 'Choose a workflow first' : 'Select a step';

  return (
    <Select
      value={value}
      onValueChange={(next) => {
        if (next) onChange(next);
      }}
      disabled={disabled || !workflow || steps.length === 0}
    >
      <SelectTrigger aria-label={ariaLabel ?? 'Step'}>
        {/* See `WorkflowSelect`'s identical comment: a `SelectValue`
            children function always wins over `placeholder` in Base UI, so
            the empty case is handled here, not left to the prop. */}
        <SelectValue>
          {(id: string | undefined) =>
            id
              ? id === ANY_STEP
                ? ANY_STEP_LABEL
                : (steps.find((step) => step.id === id)?.name ?? id)
              : placeholder
          }
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {allowAnyStep ? (
          <SelectItem value={ANY_STEP} className="italic">
            {ANY_STEP_LABEL}
          </SelectItem>
        ) : null}
        {steps.map((step) => (
          <SelectItem key={step.id} value={step.id}>
            {step.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
